/**
 * Create quote modal (19.2).
 * Customer, contact, parent item, quantity.
 */

import { useCallback, useEffect, useState } from 'react';
import { createQuote } from '../../api/quotes';
import { fetchCustomers, fetchParentItems } from '../../api/jobs';
import { Modal, Button } from '../common';

export default function CreateQuoteModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    customer: '', customer_contact: '', parent_item: '', quantity: 1, notes: '',
  });
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [parentItems, setParentItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadDropdowns = useCallback(async () => {
    try {
      const [custData, piData] = await Promise.all([
        fetchCustomers(),
        fetchParentItems(),
      ]);
      setCustomers(custData.data || custData.results || custData || []);
      setParentItems(piData.data || piData.results || piData || []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadDropdowns();
      setForm({ customer: '', customer_contact: '', parent_item: '', quantity: 1, notes: '' });
      setError(null);
    }
  }, [open, loadDropdowns]);

  useEffect(() => {
    if (form.customer) {
      const cust = customers.find((c) => c.id === form.customer);
      setContacts(cust?.contacts || []);
      setForm((prev) => ({ ...prev, customer_contact: '' }));
    } else {
      setContacts([]);
    }
  }, [form.customer, customers]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.customer || !form.parent_item) {
      setError('Customer and parent item are required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = { ...form };
      if (!payload.customer_contact) delete payload.customer_contact;
      if (!payload.notes) delete payload.notes;
      const quote = await createQuote(payload);
      onCreated?.(quote);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create quote');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Create New Quote" maxWidth="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            value={form.customer}
            onChange={(e) => setForm({ ...form, customer: e.target.value })}
          >
            <option value="">Select customer...</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {contacts.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.customer_contact}
              onChange={(e) => setForm({ ...form, customer_contact: e.target.value })}
            >
              <option value="">Select contact...</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{c.name} {c.is_primary ? '(Primary)' : ''}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Parent Item</label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            value={form.parent_item}
            onChange={(e) => setForm({ ...form, parent_item: e.target.value })}
          >
            <option value="">Select parent item...</option>
            {parentItems.map((pi) => (
              <option key={pi.id} value={pi.id}>
                {pi.parent_item_no} — {pi.description}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
          <input
            type="number"
            min="1"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            value={form.quantity}
            onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 1 })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={loading}>Create Quote</Button>
        </div>
      </form>
    </Modal>
  );
}
